import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandWhatsapp,
  IconRobot,
  IconSpeakerphone,
  IconUsers,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '../lib/trpc';

function PublicNav() {
  return (
    <Box
      component="nav"
      style={{
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        background: 'white',
      }}
    >
      <Container size="lg" py="sm">
        <Group justify="space-between">
          <Anchor component={Link} to="/" underline="never">
            <Title order={4}>LeadSync</Title>
          </Anchor>
          <Group gap="lg" visibleFrom="sm">
            <Anchor component={Link} to="/features" underline="never" c="dark">
              Features
            </Anchor>
            <Anchor component={Link} to="/pricing" underline="never" c="dark">
              Pricing
            </Anchor>
            <Anchor component={Link} to="/about" underline="never" c="dark">
              About
            </Anchor>
            <Anchor component={Link} to="/contact" underline="never" c="dark">
              Contact
            </Anchor>
          </Group>
          <Group>
            <Button component={Link} to="/auth/login" variant="default" size="sm">
              Sign in
            </Button>
            <Button component={Link} to="/auth/register" size="sm">
              Get started
            </Button>
          </Group>
        </Group>
      </Container>
    </Box>
  );
}

function PublicFooter() {
  return (
    <Box
      mt="xl"
      style={{ borderTop: '1px solid var(--mantine-color-gray-2)', background: 'white' }}
    >
      <Container size="lg" py="md">
        <Group justify="space-between" wrap="wrap">
          <Text size="sm" c="dimmed">
            © {new Date().getFullYear()} LeadSync.
          </Text>
          <Group gap="md">
            <Anchor component={Link} to="/legal/privacy" size="sm" c="dimmed">
              Privacy
            </Anchor>
            <Anchor component={Link} to="/legal/terms" size="sm" c="dimmed">
              Terms
            </Anchor>
            <Anchor component={Link} to="/legal/data-deletion" size="sm" c="dimmed">
              Data deletion
            </Anchor>
            <Anchor component={Link} to="/contact" size="sm" c="dimmed">
              Contact
            </Anchor>
          </Group>
        </Group>
      </Container>
    </Box>
  );
}

function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Authed users get bounced to their console — landing is for visitors.
  // The query is fire-and-forget; if the cookie is missing or invalid
  // it 401s and we just render the marketing page like normal.
  const me = trpc.auth.me.useQuery(undefined, { retry: false });
  useEffect(() => {
    if (!me.isSuccess || !me.data) return;
    if (me.data.roles?.includes('SuperAdmin')) navigate({ to: '/central' });
    else if (me.data.roles?.includes('VendorAdmin') || me.data.roles?.includes('TeamMember')) {
      navigate({ to: '/vendor' });
    }
  }, [me.isSuccess, me.data, navigate]);
  return (
    <Box>
      <PublicNav />
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Container size="lg" py="xl">
          <Stack gap="xl" align="center" mt="xl">
            <Badge variant="light" size="lg">
              WhatsApp · Messenger · Instagram
            </Badge>
            <Title order={1} ta="center" maw={760}>
              {t('App_Name')} — one inbox for every conversation that matters.
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={680}>
              Multi-tenant WhatsApp + Facebook + Instagram marketing platform. Reach customers where
              they already are, automate the obvious, and let your team focus on the calls that
              actually need them.
            </Text>

            <Group>
              <Button component={Link} to="/auth/register" size="md">
                Start free trial
              </Button>
              <Button component={Link} to="/contact" variant="default" size="md">
                Talk to us
              </Button>
            </Group>

            <Divider w="100%" />

            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" w="100%">
              <Card>
                <Group>
                  <IconBrandWhatsapp />
                  <Text fw={600}>WhatsApp Cloud API</Text>
                </Group>
                <Text size="sm" c="dimmed" mt="xs">
                  Templates, chat inbox, flows, campaigns, webhooks — everything official.
                </Text>
              </Card>
              <Card>
                <Group>
                  <IconBrandFacebook />
                  <Text fw={600}>Facebook Messenger</Text>
                </Group>
                <Text size="sm" c="dimmed" mt="xs">
                  Page OAuth, inbox, page-insights analytics, webhooks.
                </Text>
              </Card>
              <Card>
                <Group>
                  <IconBrandInstagram />
                  <Text fw={600}>Instagram DMs</Text>
                </Group>
                <Text size="sm" c="dimmed" mt="xs">
                  IG Business account binding, DM inbox, reactions + postbacks.
                </Text>
              </Card>
              <Card>
                <Group>
                  <IconSpeakerphone />
                  <Text fw={600}>Campaigns</Text>
                </Group>
                <Text size="sm" c="dimmed" mt="xs">
                  Scheduled mass messaging with delivery + click-tracking reports.
                </Text>
              </Card>
              <Card>
                <Group>
                  <IconRobot />
                  <Text fw={600}>Bot flows</Text>
                </Group>
                <Text size="sm" c="dimmed" mt="xs">
                  Visual flow builder, keyword triggers, AI fallbacks.
                </Text>
              </Card>
              <Card>
                <Group>
                  <IconUsers />
                  <Text fw={600}>Team + permissions</Text>
                </Group>
                <Text size="sm" c="dimmed" mt="xs">
                  Invite teammates, scope what they can do, audit every change.
                </Text>
              </Card>
            </SimpleGrid>
          </Stack>
        </Container>
      </motion.div>
      <PublicFooter />
    </Box>
  );
}

export const Route = createFileRoute('/')({ component: Landing });
